import { useMutation, useQuery } from '@tanstack/react-query'
import { httpClient, ROOT_URL } from '../App'

export const useGetMetadataCurrency = () => {
  return useQuery<{ data: [] }>([`/api/v1/currencies`], async () =>
    getMetadataCurrency()
  )
}

export const useGetMetadataCurrencyToken = (networkId?: number) => {
  return useQuery<{ data: { data: [] } }>(
    [`/api/v1/asset-config-groups/crypto`, networkId],
    async () => getMetadataCurrencyToken(networkId)
  )
}

export const useGetAllMetadataCurrency = () => {
  return useQuery<{ data: { data: [] } }>(
    [`/api/v1/asset-config-groups/get-all`],
    async () => getAllMetadataCurrency()
  )
}

export function getAllMetadataCurrency() {
  return httpClient.get(
    `${ROOT_URL}/api/v1/asset-config-groups/get-all?_start=0&end=100`
  )
}

export const useGetMetadataNetwork = (currencyId?: number) => {
  return useQuery<{ data: [] }>([`/api/v1/networks`, currencyId], async () =>
    getMetadataNetwork(currencyId)
  )
}

export const useGetDetailBalanceCrypto = (id?: number, search?: string) => {
  return useQuery<{
    data: {
      data: {
        id: number
        symbol: string
        name: string
        iconPath: string
        totalBalance: number
        decimals: number
        amounts: [
          {
            USD: number
            decimals: number | null
          },
          {
            EUR: number
            decimals: number | null
          },
        ]
      }[]
    }
  }>(
    [`/api/v1/asset-config-groups/total-assets/get-all`, search, id],
    async () => getAllCryptoCurrency(search),
    {
      enabled: !!id,
    }
  )
}

export const useGetAllCryptoCurrency = (
  search?: string,
  method?: string,
  enabled = true
) => {
  return useQuery<{
    data: {
      data: {
        id: number
        symbol: string
        name: string
        iconPath: string
        totalBalance: number
        decimals: number
        amount: number
      }[]
    }
  }>(
    [`/api/v1/asset-config-groups/total-assets/get-all`, search, method],
    async () => getAllCryptoCurrency(search, method),
    { enabled }
  )
}

export const useGetAllFiatCurrency = (search: string) => {
  return useQuery<{
    data: {
      data: {
        id: number
        symbol: string
        name: string
        iconPath: string
        totalBalance: number
        decimals: number
        amounts: [
          {
            USD: number
            decimals: number | null
          },
          {
            EUR: number
            decimals: number | null
          },
        ]
      }[]
    }
  }>([`/api/v1/asset-config-groups/fiat/get-all`, search], async () =>
    getAlFiatCurrency(search)
  )
}

export function getMetadataCurrency() {
  return httpClient.get(`${ROOT_URL}/api/v1/currencies`)
}

export function getMetadataCurrencyToken(networkId?: number) {
  if (networkId)
    return httpClient.get(
      `${ROOT_URL}/api/v1/asset-config-groups/crypto?_networkId=${networkId}`
    )
  return httpClient.get(`${ROOT_URL}/api/v1/asset-config-groups/crypto`)
}

export function getMetadataNetwork(currencyId?: number) {
  if (currencyId)
    return httpClient.get(
      `${ROOT_URL}/api/v1/networks?_currencyId=${currencyId}`
    )
  return httpClient.get(`${ROOT_URL}/api/v1/networks`)
}

export function getAllCryptoCurrency(search?: string, method?: string) {
  const param = new URLSearchParams()
  if (search) param.append('_like', String(search))
  if (method) param.append('_type', String(method))
  return httpClient.get(
    `${ROOT_URL}/api/v1/asset-config-groups/total-assets/get-all?${param.toString()}`
  )
}

export function getAlFiatCurrency(search: string) {
  if (!search)
    return httpClient.get(`${ROOT_URL}/api/v1/asset-config-groups/fiat/get-all`)
  return httpClient.get(
    `${ROOT_URL}/api/v1/asset-config-groups/fiat/get-all?_like=${search}`
  )
}

export const useDepositCrypto = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: depositCrypto,
  })
}

export function depositCrypto(body: {
  assetConfigGroupId: number
  networkId: number
}) {
  return httpClient.post(`${ROOT_URL}/api/v1/user-assets/create`, body)
}

export const useGetTotalAsset = (enabled = true) => {
  return useQuery<{
    data: {
      data: [
        {
          USD: number
          decimals: number | null
        },
        {
          EUR: number
          decimals: number | null
        },
      ]
    }
  }>(
    [`/api/v1/asset-config-groups/get-total-assets`],
    async () => getTotalAsset(),
    { enabled }
  )
}

export function getTotalAsset() {
  return httpClient.get(
    `${ROOT_URL}/api/v1/asset-config-groups/get-total-assets`
  )
}

export const useGetTransaction = (params: {
  start: number
  end: number
  tokenId?: number
  id?: number
}) => {
  return useQuery<{
    data: {
      data: {
        total: number
        page: number
        pageSize: number
        totalPages: number
        items: []
      }
    }
  }>(
    [
      `/api/v1/transactions/get-list`,
      params.start,
      params.end,
      params.tokenId,
      params.id,
    ],
    async () => getTransaction(params),
    { enabled: !!params.tokenId && !!params.id }
  )
}

export const getTransaction = (params: {
  start: number
  end: number
  tokenId?: number
}) => {
  const param = new URLSearchParams()
  param.append('_start', String(params.start))
  param.append('_end', String(params.end))
  if (params.tokenId) param.append('_currencyId', String(params.tokenId))
  return httpClient.get(
    `${ROOT_URL}/api/v1/transactions/get-list?${param.toString()}`
  )
}

export const useGetListOrder = (params: {
  start: number
  end: number
  name?: string
  status?: string
  _gte?: string
  _lte?: string
  _type_1?: string
  _type_2?: string
  _timeZone?: string
}) => {
  return useQuery<{
    data: {
      data: {
        total: number
        page: number
        pageSize: number
        totalPages: number
        items: []
      }
    }
  }>(
    [
      `/api/v1/orders/crypto/get-list`,
      params.start,
      params.end,
      params.status,
      params.name,
      params._gte,
      params._lte,
      params._type_1,
      params._type_2,
      params._timeZone,
    ],
    async () => getListOrder(params)
  )
}

export const getListOrder = (params: {
  start: number
  end: number
  name?: string
  status?: string
  _gte?: string
  _lte?: string
  _type_1?: string
  _type_2?: string
  _timeZone?: string
}) => {
  const param = new URLSearchParams()
  param.append('_start', String(params.start))
  param.append('_end', String(params.end))
  if (params.name) param.append('_like', params.name)
  if (params.status) param.append('_status', params.status)
  if (params._gte) param.append('_gte', params._gte)
  if (params._lte) param.append('_lte', params._lte)
  if (params._type_1) param.append('_type', params._type_1)
  if (params._type_2) param.append('_type', params._type_2)
  if (params._timeZone) param.append('_type', params._timeZone)
  return httpClient.get(
    `${ROOT_URL}/api/v1/orders/crypto/get-list?${param.toString()}`
  )
}

export const useGetOneTransaction = (orderId: string) => {
  return useQuery<{
    data: {
      data: {
        id: number
        orderId: string
        decimals: number
        status: string
        amount: string
        chainType: string
        symbol: string
        beneficiary: string
        remitterRegNo: string
        remitterFullName: string
        remitterCompanyAddress: string
        remitterCountry: string
        createdAt: string
        fees: string
        reason: string
        type: string
        address: string
        printableFormUrl: string
      }
    }
  }>(
    [`/api/v1/orders/get-one`, orderId],
    async () => getOneTransaction(orderId),
    {
      enabled: !!orderId,
    }
  )
}

export const getOneTransaction = (orderId: string) => {
  return httpClient.get(`${ROOT_URL}/api/v1/orders/get-one/${orderId}`)
}

export const useGetDetailTransaction = (orderId: string) => {
  return useQuery<{
    data: {
      data: {
        id: number
        orderId: string
        status: string
        amount: string
        chainType: string
        currency: string
        beneficiary: string
        remitter: string
        depositAddress: string
        createdAt: string
        fees: string
        reason: string
        type: string
        withdrawalAddress: string
      }
    }
  }>(
    [`/api/v1/transactions/get-one/`, orderId],
    async () => getDetailTransaction(orderId),
    {
      enabled: !!orderId,
    }
  )
}

export const getDetailTransaction = (id: string) => {
  return httpClient.get(`${ROOT_URL}/api/v1/transactions/get-one/${id}`)
}

export const useGetExchangeRateBulk = (enabled = true) => {
  return useQuery<{ data: any }>(
    [`/api/v1/exchange-rates/bulk`],
    async () => getExchangeRateBulk(),
    { enabled }
  )
}

export const getExchangeRateBulk = () => {
  return httpClient.get(`${ROOT_URL}/api/v1/exchange-rates/bulk`)
}

export const useGetListHistory = (params: {
  start: number
  end: number
  name?: string
  status?: string
  _gte?: string
  _lte?: string
  _type_1?: string
  _type_2?: string
  _timeZone?: string
  _historyType?: string
}) => {
  return useQuery<{
    data: {
      data: {
        total: number
        page: number
        pageSize: number
        totalPages: number
        items: []
      }
    }
  }>(
    [
      `/api/v1/history/get-list`,
      params.start,
      params.end,
      params.status,
      params.name,
      params._gte,
      params._lte,
      params._type_1,
      params._type_2,
      params._timeZone,
      params._historyType,
    ],
    async () => getListCryptoHistory(params)
  )
}

export const getListCryptoHistory = (params: {
  start: number
  end: number
  name?: string
  status?: string
  _gte?: string
  _lte?: string
  _type_1?: string
  _type_2?: string
  _timeZone?: string
  _historyType?: string
}) => {
  const param = new URLSearchParams()
  param.append('_start', String(params.start))
  param.append('_end', String(params.end))
  if (params.name) param.append('_like', params.name)
  if (params.status) param.append('_status', params.status)
  if (params._gte) param.append('_gte', params._gte)
  if (params._lte) param.append('_lte', params._lte)
  if (params._type_1) param.append('_type', params._type_1)
  if (params._type_2) param.append('_type', params._type_2)
  if (params._timeZone) param.append('_type', params._timeZone)
  if (params._historyType) param.append('_historyType', params._historyType)
  return httpClient.get(
    `${ROOT_URL}/api/v1/history/get-list?${param.toString()}`
  )
}
